import { useEffect, useState } from "react";
import { TrendingUp, Sparkles } from "lucide-react";
import api from "@/lib/api";

const APP_META = {
    upi: { label: "Any UPI", color: "bg-slate-500", text: "text-slate-700" },
    gpay: { label: "Google Pay", color: "bg-blue-500", text: "text-blue-700" },
    phonepe: { label: "PhonePe", color: "bg-purple-500", text: "text-purple-700" },
    paytm: { label: "Paytm", color: "bg-cyan-500", text: "text-cyan-700" },
};

const APP_ORDER = ["phonepe", "gpay", "paytm", "upi"];

function StackedBar({ pct, total }) {
    if (!total) {
        return <div className="h-2.5 bg-slate-100 rounded-full" />;
    }
    return (
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden flex">
            {APP_ORDER.map((app) => {
                const v = pct[app] || 0;
                if (!v) return null;
                return (
                    <div key={app}
                        className={`${APP_META[app].color} h-full transition-all`}
                        style={{ width: `${v}%` }}
                        title={`${APP_META[app].label}: ${v}%`}
                    />
                );
            })}
        </div>
    );
}

/**
 * Renders the live distribution of which UPI app customers tapped to close
 * a deposit, plus a per-amount-bucket breakdown so partners can decide which
 * intent button to surface first per ticket size.
 *
 * @param {string=} partnerId  — admin-mode: filter by a single partner. Omit for all-partners.
 * @param {string} scope       — "partner" | "admin"
 */
export default function PaidViaWidget({ partnerId, scope = "partner" }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        const url = scope === "admin"
            ? `/admin/analytics/paid-via${partnerId ? `?partner_id=${encodeURIComponent(partnerId)}` : ""}`
            : "/partner/analytics/paid-via";
        api.get(url).then((r) => setData(r.data)).catch(() => setData({ error: true }));
    }, [partnerId, scope]);

    if (!data) {
        return (
            <div className="card p-5">
                <div className="text-sm text-slate-400">Loading payment-app distribution…</div>
            </div>
        );
    }
    if (data.error) {
        return (
            <div className="card p-5">
                <div className="text-sm text-rose-500">Failed to load analytics.</div>
            </div>
        );
    }

    const total = data.overall.total;

    return (
        <div className="card p-5" data-testid="paid-via-widget">
            <div className="flex items-start justify-between mb-1">
                <div>
                    <h3 className="font-semibold flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-emerald-600" /> Payment-app share
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                        Which UPI app the customer tapped + tap-to-success conversion · last {data.window_days} days
                        {data.strategy === "conversion_rate" && (
                            <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700 font-medium" data-testid="paid-via-strategy-pill">
                                · auto-routing by conversion
                            </span>
                        )}
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-[11px] text-slate-400 uppercase tracking-wider">Taps</div>
                    <div className="text-lg font-bold font-mono leading-tight">{total}</div>
                </div>
            </div>

            {total === 0 ? (
                <div className="mt-3 rounded-md border border-dashed border-slate-200 bg-slate-50 p-4 text-center">
                    <div className="text-sm text-slate-500">
                        No payment-app data yet — once customers tap an intent button on the payment page, this widget fills with their choices.
                    </div>
                </div>
            ) : (
                <>
                    {/* Overall distribution */}
                    <div className="mt-3">
                        <StackedBar pct={data.overall.pct} total={total} />
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {APP_ORDER.map((app) => {
                                const pct = data.overall.pct[app] || 0;
                                const taps = data.overall[app] || 0;
                                const conv = data.overall.completion_rate?.[app];
                                return (
                                    <div key={app} className="flex items-start gap-2"
                                         data-testid={`paid-via-app-${app}`}>
                                        <span className={`mt-1 w-2.5 h-2.5 rounded-sm ${APP_META[app].color}`} />
                                        <div className="text-xs">
                                            <div className={`font-semibold ${APP_META[app].text}`}>{APP_META[app].label}</div>
                                            <div className="text-slate-500 font-mono">{pct}% · {taps} taps</div>
                                            {conv != null && (
                                                <div data-testid={`paid-via-completion-${app}`}
                                                     className={`text-[10px] font-mono mt-0.5 ${conv >= 80 ? "text-emerald-700" : conv >= 50 ? "text-slate-600" : "text-amber-700"}`}>
                                                    {conv}% completed
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {data.overall.overall_completion != null && (
                            <div data-testid="paid-via-overall-completion" className="mt-3 text-[11px] text-slate-500 border-t border-slate-100 pt-2">
                                Overall tap-to-success conversion · <span className="font-mono font-medium text-slate-700">{data.overall.overall_completion}%</span>
                            </div>
                        )}
                    </div>

                    {/* Per-bucket breakdown */}
                    <div className="mt-5 pt-4 border-t border-slate-100">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> By ticket size
                        </div>
                        <div className="space-y-2.5">
                            {data.by_bucket.map((b) => (
                                <div key={b.bucket} data-testid={`paid-via-bucket-${b.bucket}`} className="space-y-1">
                                    <div className="flex items-center justify-between text-[11px]">
                                        <div className="font-medium text-slate-700">
                                            {b.label}
                                            <span className="ml-1.5 text-slate-400 font-mono">· {b.distribution.total}</span>
                                        </div>
                                        {b.recommended_app ? (
                                            <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${APP_META[b.recommended_app].text}`}>
                                                <Sparkles className="w-2.5 h-2.5" /> {APP_META[b.recommended_app].label}
                                            </span>
                                        ) : (
                                            <span className="text-[10px] text-slate-400">—</span>
                                        )}
                                    </div>
                                    <StackedBar pct={b.distribution.pct} total={b.distribution.total} />
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
